import type {
  AttachReadingResponse,
  ChatBootstrapChart,
  ChatBootstrapResponse,
  ChatDailyGuidance,
  ChatDailyGuidanceResponse,
  ChatResponseFull,
  ContactCaptureRequest,
  ContactCaptureResponse,
  NextAction,
} from "@ishvaram/api-client";
import { getTurnstileToken } from "@/lib/turnstile";
import { getOrCreateBootstrapDistinctId } from "@/lib/analytics-identity";
import { apiRequest, authJsonHeaders } from "./_fetch";

export type {
  AttachReadingResponse,
  ChatBootstrapChart,
  ChatBootstrapResponse,
  ChatDailyGuidance,
  ChatResponseFull,
  ContactCaptureRequest,
  ContactCaptureResponse,
  NextAction,
};

/**
 * Load everything the dedicated /chat/<readingId> surface needs in one
 * round-trip: the greeting, present doshas (chart context), and the persisted
 * conversation (real reload/resume). Backed by GET /chat/{reading_id}/bootstrap.
 */
export async function getChatBootstrap(
  readingId: string,
  language = "en",
  concern?: string
): Promise<ChatBootstrapResponse> {
  const concernParam = concern ? `&concern=${encodeURIComponent(concern)}` : "";
  const cid = clientId();
  const clientParam = cid ? `&client_id=${encodeURIComponent(cid)}` : "";
  return apiRequest<ChatBootstrapResponse>(
    `/chat/${encodeURIComponent(readingId)}/bootstrap?language=${encodeURIComponent(language)}${concernParam}${clientParam}`,
    { headers: await authJsonHeaders() }
  );
}

/** Optional person-day enrichment, fetched only after the core chat is interactive. */
export async function getChatDailyGuidance(
  readingId: string,
  language = "en"
): Promise<ChatDailyGuidance | null> {
  const cid = clientId();
  const clientParam = cid ? `&client_id=${encodeURIComponent(cid)}` : "";
  const response = await apiRequest<ChatDailyGuidanceResponse>(
    `/chat/${encodeURIComponent(readingId)}/daily-guidance?language=${encodeURIComponent(language)}${clientParam}`,
    { headers: await authJsonHeaders() }
  );
  return response.guidance ?? null;
}

/** Returning-visitor resume-card context: the seeker's remembered concern. */
export interface ChatResumeContext {
  has_memory: boolean;
  /** Already-customer-facing warm callback, or null when nothing is recalled. */
  opener: string | null;
}

/** Explicitly attach this bearer reading to the current verified auth subject. */
export async function attachReadingToMyJyotishi(readingId: string): Promise<AttachReadingResponse> {
  return apiRequest<AttachReadingResponse>(
    `/account/readings/${encodeURIComponent(readingId)}/attach`,
    {
      method: "POST",
      headers: await authJsonHeaders(),
    }
  );
}

/**
 * Load the remembered-concern hook for the returning-visitor resume card.
 * Backed by GET /chat/{reading_id}/resume-context — the reading id is itself
 * the bearer capability (no auth). The first-party client_id rides along (same
 * id the chat turn sends, ADR-0021/0038 anon scope) so a returning seeker on a
 * FRESH reading still gets their same-browser continuity opener. Returns
 * `{ has_memory:false, opener:null }` on a cold reading; the caller keeps its
 * generic copy on that or any error.
 */
export async function getChatResumeContext(
  readingId: string,
  language = "en"
): Promise<ChatResumeContext> {
  const cid = clientId();
  const clientParam = cid ? `&client_id=${encodeURIComponent(cid)}` : "";
  return apiRequest<ChatResumeContext>(
    `/chat/${encodeURIComponent(readingId)}/resume-context?lang=${encodeURIComponent(language)}${clientParam}`,
    { headers: await authJsonHeaders() }
  );
}

export async function sendChatMessage(
  readingId: string,
  message: string,
  sessionId?: string
): Promise<{ response: string; session_id: string }> {
  return apiRequest<{ response: string; session_id: string }>("/chat", {
    method: "POST",
    headers: await authJsonHeaders(),
    body: JSON.stringify({
      reading_id: readingId,
      turn_id: crypto.randomUUID(),
      message,
      session_id: sessionId,
      stream: false,
      turnstile_token: getTurnstileToken() ?? undefined,
    }),
  });
}

export interface SendChatMessageFullOptions {
  sessionId?: string;
  language?: string;
  personaId?: string;
  specialistHint?: string;
  concern?: string;
}

export async function sendChatMessageFull({
  readingId,
  message,
  sessionId,
  language = "en",
  personaId,
  specialistHint,
  concern,
}: SendChatMessageFullOptions & {
  readingId: string;
  message: string;
}): Promise<ChatResponseFull> {
  return apiRequest<ChatResponseFull>("/chat", {
    method: "POST",
    headers: await authJsonHeaders(),
    body: JSON.stringify({
      reading_id: readingId,
      turn_id: crypto.randomUUID(),
      message,
      session_id: sessionId,
      language,
      stream: false,
      turnstile_token: getTurnstileToken() ?? undefined,
      ...(personaId ? { persona_id: personaId } : {}),
      ...(specialistHint ? { specialist_hint: specialistHint } : {}),
      ...(concern ? { concern } : {}),
      // First-party browser id → anonymous seeker identity for user-level
      // memory (ADR-0021): "he remembers you" across readings.
      ...(clientId() ? { client_id: clientId() } : {}),
    }),
  });
}

export async function captureReadingContact(
  readingId: string,
  body: ContactCaptureBody
): Promise<{ success: boolean; captured_email: boolean; captured_phone: boolean }> {
  return apiRequest<{ success: boolean; captured_email: boolean; captured_phone: boolean }>(
    `/readings/${encodeURIComponent(readingId)}/contact`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
}

export async function captureMatchingContact(
  matchId: string,
  body: ContactCaptureRequest
): Promise<ContactCaptureResponse> {
  return apiRequest<ContactCaptureResponse>(`/matching/${encodeURIComponent(matchId)}/contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type ContactCaptureBody = ContactCaptureRequest;

/**
 * Reading-level accuracy affirmation for the chat reading surface — the rule-44
 * calibration signal (surface="chat"), feeding vedic_reading_ratings keyed by
 * reading_id. Mirrors rateReading() on the report surface so both feed one
 * per-rule accuracy aggregation. Best-effort: callers ignore failures.
 */
export async function rateChatReading(
  readingId: string,
  data: { thumbs?: 1 | -1; stars?: number; comment?: string }
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`/chat/${encodeURIComponent(readingId)}/rate-reading`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

/**
 * Per-message thumbs for a single assistant reply — the chat-quality ground
 * truth (rule 53 Layer 3). Lands in the canonical `vedic_feedback` sink with
 * source="web_chat_message" and entity_type="chat_message".
 * Mirrors rateChatReading() — same client; the control retains a retry path on
 * failure. Backend: POST /chat/{readingId}/messages/{messageId}/rate
 * (MessageRatingRequest: rating "up"|"down", optional classified reasons).
 */
export async function rateChatMessage(
  readingId: string,
  messageId: string,
  rating: "up" | "down",
  reasons?: string[]
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `/chat/${encodeURIComponent(readingId)}/messages/${encodeURIComponent(messageId)}/rate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rating, reasons }),
    }
  );
}

import { streamChat, type ChatStreamHandlers } from "@/lib/streaming/chat-stream";
import { API_BASE } from "./_fetch";

export interface StreamChatMessageInput {
  readingId: string;
  message: string;
  sessionId?: string;
  language?: string;
  personaId?: string;
  specialistHint?: string;
  concern?: string;
  handlers: ChatStreamHandlers;
  signal?: AbortSignal;
}

/**
 * Open a streaming chat connection. Resolves when the stream ends.
 * Handlers receive text deltas (rAF-coalesce in the consumer to avoid
 * re-rendering per token) and structured side-events (products,
 * next_action). Returns void; the consumer keeps partial text in a ref.
 *
 * Cancellation via AbortSignal — pass an AbortController.signal and call
 * .abort() to cancel mid-stream (user navigates, user sends a new message
 * before the previous one finishes streaming, etc.).
 */
export async function streamChatMessage(input: StreamChatMessageInput): Promise<void> {
  const {
    readingId,
    message,
    sessionId,
    language = "en",
    personaId,
    specialistHint,
    concern,
    handlers,
    signal,
  } = input;
  const headers = await authJsonHeaders();
  await streamChat({
    url: `${API_BASE}/chat`,
    headers,
    body: {
      reading_id: readingId,
      turn_id: crypto.randomUUID(),
      message,
      session_id: sessionId,
      language,
      stream: true,
      turnstile_token: getTurnstileToken() ?? undefined,
      ...(personaId ? { persona_id: personaId } : {}),
      ...(specialistHint ? { specialist_hint: specialistHint } : {}),
      ...(concern ? { concern } : {}),
      // First-party browser id → anonymous seeker identity for user-level
      // memory (ADR-0021): "he remembers you" across readings.
      ...(clientId() ? { client_id: clientId() } : {}),
    },
    handlers,
    signal,
  });
}

/** The owned first-party browser id (undefined during SSR / storage-blocked). */
function clientId(): string | undefined {
  return getOrCreateBootstrapDistinctId();
}
